import React, { useEffect, useState } from "react";
import { api, fmtDateTime, errMsg } from "@/lib/api";
import { Empty, Skeletons } from "@/components/Primitives";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { UserPlus, Trash2, Mic2, ShieldCheck, Loader2, Search } from "lucide-react";

export default function Users() {
  const { can, user: me } = useAuth();
  const [rows, setRows] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [del, setDel] = useState(null);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({ name: "", email: "", password: "", role: "participant", division: "", organization: "" });
  const manage = can(["super_admin", "org_admin"]);

  const load = async () => {
    setLoading(true);
    try {
      const [u, r] = await Promise.all([api.get("/users", { params: { q } }), api.get("/roles")]);
      setRows(u.data); setRoles(r.data);
    } catch (e) { toast.error(errMsg(e)); } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const create = async () => {
    setBusy(true);
    try {
      await api.post("/users", f);
      toast.success("Pengguna dibuat");
      setOpen(false);
      setF({ name: "", email: "", password: "", role: "participant", division: "", organization: "" });
      await load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const patch = async (id, body) => {
    try { await api.put(`/users/${id}`, body); toast.success("Pengguna diperbarui"); await load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const remove = async () => {
    try { await api.delete(`/users/${del.id}`); toast.success("Pengguna dihapus"); setDel(null); await load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="space-y-7" data-testid="users-page">
      <div className="flex flex-wrap items-end justify-between gap-5">
        <div>
          <p className="label-xs">Kontrol Akses</p>
          <h1 className="mt-2.5 font-display text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900">User &amp; Role</h1>
          <p className="mt-2 text-sm text-slate-500">{rows.length} pengguna · Role Based Access Control aktif.</p>
        </div>
        {manage && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="h-11 rounded-xl grad-brand text-white px-5" data-testid="add-user-btn"><UserPlus size={16} className="mr-2" /> Tambah Pengguna</Button>
            </DialogTrigger>
            <DialogContent data-testid="add-user-dialog">
              <DialogHeader><DialogTitle className="font-display">Tambah Pengguna</DialogTitle></DialogHeader>
              <div className="grid gap-4">
                {[["name", "Nama Lengkap", "text"], ["email", "Email", "email"], ["password", "Password", "password"], ["division", "Divisi", "text"], ["organization", "Organisasi", "text"]].map(([k, l, t]) => (
                  <div key={k} className="space-y-2">
                    <Label className="text-xs font-semibold text-slate-700">{l}</Label>
                    <Input type={t} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} className="h-11 rounded-xl" data-testid={`new-user-${k}`} />
                  </div>
                ))}
                <div className="space-y-2">
                  <Label className="text-xs font-semibold text-slate-700">Role</Label>
                  <Select value={f.role} onValueChange={(v) => setF({ ...f, role: v })}>
                    <SelectTrigger className="h-11 rounded-xl" data-testid="new-user-role"><SelectValue /></SelectTrigger>
                    <SelectContent>{roles.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter>
                <Button className="rounded-xl grad-brand text-white" onClick={create} disabled={busy || !f.name || !f.email || !f.password} data-testid="submit-user-btn">
                  {busy ? <Loader2 size={16} className="mr-2 animate-spin" /> : null} Simpan
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="clay p-5">
        <form className="flex gap-3" onSubmit={(e) => { e.preventDefault(); load(); }}>
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari nama atau email…" className="h-11 pl-10 rounded-xl" data-testid="user-search-input" />
          </div>
          <Button type="submit" variant="outline" className="h-11 rounded-xl border-slate-200" data-testid="user-search-btn">Cari</Button>
        </form>
      </div>

      <div className="clay overflow-hidden" data-testid="users-table-card">
        {loading ? <div className="p-6"><Skeletons rows={5} className="h-14" /></div> : rows.length === 0 ? (
          <Empty icon={ShieldCheck} title="Tidak ada pengguna" sub="Tambahkan pengguna untuk mulai mengelola akses." />
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent border-slate-100">
                  <TableHead className="p-4 label-xs">Pengguna</TableHead>
                  <TableHead className="p-4 label-xs">Role</TableHead>
                  <TableHead className="p-4 label-xs">Divisi</TableHead>
                  <TableHead className="p-4 label-xs">Voiceprint</TableHead>
                  <TableHead className="p-4 label-xs">Status</TableHead>
                  <TableHead className="p-4 label-xs">Dibuat</TableHead>
                  {manage && <TableHead className="p-4 label-xs text-right">Aksi</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((u) => (
                  <TableRow key={u.id} className="border-slate-100 hover:bg-slate-50/70 transition-colors" data-testid={`user-row-${u.id}`}>
                    <TableCell className="p-4">
                      <div className="flex items-center gap-3">
                        <span className="grid place-items-center h-9 w-9 shrink-0 rounded-full grad-brand text-white text-[11px] font-semibold">{u.name.slice(0, 2).toUpperCase()}</span>
                        <div className="min-w-0">
                          <p className="truncate text-sm font-semibold text-slate-800">{u.name}</p>
                          <p className="truncate text-xs text-slate-500">{u.email}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="p-4">
                      {manage && u.id !== me?.id ? (
                        <Select value={u.role} onValueChange={(v) => patch(u.id, { role: v })}>
                          <SelectTrigger className="h-9 w-[170px] rounded-xl text-xs" data-testid={`user-role-select-${u.id}`}><SelectValue /></SelectTrigger>
                          <SelectContent>{roles.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
                        </Select>
                      ) : (
                        <span className="rounded-full bg-brand-50 px-2.5 py-1 text-xs font-medium text-brand-700">{u.role_label}</span>
                      )}
                    </TableCell>
                    <TableCell className="p-4 text-sm text-slate-600">{u.division || "—"}</TableCell>
                    <TableCell className="p-4">
                      {u.voice_enrolled
                        ? <span className="inline-flex items-center gap-1.5 text-xs text-emerald-600"><Mic2 size={13} /> Aktif</span>
                        : <span className="text-xs text-slate-400">—</span>}
                    </TableCell>
                    <TableCell className="p-4">
                      {manage && u.id !== me?.id ? (
                        <Select value={u.status} onValueChange={(v) => patch(u.id, { status: v })}>
                          <SelectTrigger className="h-9 w-[130px] rounded-xl text-xs" data-testid={`user-status-select-${u.id}`}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="active">Aktif</SelectItem>
                            <SelectItem value="suspended">Nonaktif</SelectItem>
                          </SelectContent>
                        </Select>
                      ) : <span className="text-xs text-slate-500">{u.status}</span>}
                    </TableCell>
                    <TableCell className="p-4 text-xs text-slate-500 whitespace-nowrap">{fmtDateTime(u.created_at)}</TableCell>
                    {manage && (
                      <TableCell className="p-4 text-right">
                        {u.id !== me?.id && (
                          <Button variant="ghost" size="sm" onClick={() => setDel(u)} data-testid={`delete-user-${u.id}`}><Trash2 size={15} className="text-red-500" /></Button>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      <AlertDialog open={!!del} onOpenChange={(o) => !o && setDel(null)}>
        <AlertDialogContent data-testid="delete-user-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Hapus pengguna?</AlertDialogTitle>
            <AlertDialogDescription>Akun “{del?.name}” dan voiceprint terkait akan dihapus permanen.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-user-btn">Batal</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-user-btn">Hapus</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
